// LLM generated - Model Claude 4 Sonnet
import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { useWalletRefundableBreakdown } from '~/domains/payments/hooks/useWalletRefundableBreakdown';
import { useSubmitRefund } from '~/domains/payments/hooks/useSubmitRefund';
import { useAeropayLinkedBankAccounts } from '~/domains/payments/hooks/useAeropayLinkedBankAccounts';
import { bugSnagLog } from '~/utils/bugsnag';
import { MAX_CONSISTENCY_RETRIES } from '~/domains/payments/constants';
import { dollarsToCents } from '~/domains/payments/utils/currencyRounding';

import { useWithdrawalFormContext } from '../context/withdrawal.context';

import { useWithdrawalPaymentMethods } from './useWithdrawalPaymentMethods';

// Constants
const MAX_RETRIES = MAX_CONSISTENCY_RETRIES;

type PaymentTypeModeHookProps = {
  mode: 'withdrawal' | 'refund';
  onModeChange?: (mode: 'withdrawal' | 'refund') => void;
  onModalClose?: () => void;
  isAeropayEnabled?: boolean;
};

/**
 * Main hook for managing payment type mode (withdrawal/refund) and related state
 * Consolidates all data fetching and state management for payment forms
 *
 * @param {PaymentTypeModeHookProps} props - Hook configuration
 * @returns {object} Coordinated state and handlers for payment type mode
 */
export function usePaymentTypeMode({
  mode,
  onModeChange,
  onModalClose,
  isAeropayEnabled = false,
}: PaymentTypeModeHookProps) {
  const { t } = useTranslation('payments');
  const form = useWithdrawalFormContext();
  const { paymentType, withdrawalAmount } = form.values;

  const isRefundMode = mode === 'refund';

  // All loading states that need coordination
  const { isCurrentPaymentTypeDisabled, isFetchingAvailablePaymentMethods, paidPaymentTypes } =
    useWithdrawalPaymentMethods({ paymentType });
  const { bankAccounts, isLoading: isLoadingBankAccounts } = useAeropayLinkedBankAccounts({
    enabled: isAeropayEnabled,
  });

  // Balance data - SINGLE SOURCE OF TRUTH
  const { data: walletBalance, isLoading: isLoadingWalletBalance } = useWalletBalance();
  const {
    data: refundableBreakdown,
    isFetching: isFetchingRefundableBreakdown,
    refetch: refetchRefundableBreakdown,
  } = useWalletRefundableBreakdown({
    enabled: isRefundMode, // Only enable when in refund mode
  });

  // INTERNAL CONSISTENCY CHECKING - No separate hook needed
  const hasCheckedConsistency = useRef(false);
  const retryCount = useRef(0);
  const [hasConsistencyError, setHasConsistencyError] = useState(false);

  useEffect(() => {
    // Only check consistency when in refund mode and we have data
    if (
      !isRefundMode ||
      hasCheckedConsistency.current ||
      isFetchingRefundableBreakdown ||
      !walletBalance?.refundableBalance ||
      !refundableBreakdown?.items?.length
    ) {
      return;
    }

    // Add up refundable balance from cached query (work in cents to avoid floating-point issues)
    const cachedTotalCents = refundableBreakdown.items.reduce(
      (sum, item) => sum + dollarsToCents(item.refundableBalance),
      0
    );
    const cachedTotal = cachedTotalCents / 100; // Convert back to dollars for display

    // Compare in cents to avoid floating-point precision issues
    const walletBalanceCents = dollarsToCents(walletBalance.refundableBalance);
    const difference = Math.abs(walletBalanceCents - cachedTotalCents);

    if (difference > 0) {
      // If we haven't exceeded max retries, try refetching
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        refetchRefundableBreakdown();
        return;
      }

      // If we've exceeded max retries, log to Bugsnag and show error state
      const errorDetails = {
        balance: {
          wallet: walletBalance.refundableBalance,
          cached: cachedTotal,
          difference,
        },
        retry: {
          count: retryCount.current,
          max: MAX_RETRIES,
        },
        data: {
          itemsCount: refundableBreakdown.items.length,
          items: refundableBreakdown.items,
        },
        context: {
          userId: 'unknown',
          mode: 'refund',
        },
      };

      bugSnagLog({
        errorMessage: 'Refundable balance consistency check failed after max retries',
        context: 'usePaymentTypeMode',
        severity: 'error',
        details: {
          component: 'RefundPaymentMethods',
          action: 'balance_consistency_check',
          mode: 'refund',
        },
        errorDetails,
      });

      setHasConsistencyError(true);
    } else {
      // Balance is consistent, reset error state
      setHasConsistencyError(false);
    }

    hasCheckedConsistency.current = true;
  }, [
    isRefundMode,
    walletBalance?.refundableBalance,
    refundableBreakdown,
    isFetchingRefundableBreakdown,
    refetchRefundableBreakdown,
  ]);

  // Reset consistency check when mode changes
  useEffect(() => {
    hasCheckedConsistency.current = false;
    retryCount.current = 0;
    setHasConsistencyError(false);
  }, [mode]);

  // Refund submission
  const { submitRefund, isSubmitting } = useSubmitRefund({
    onSuccess: () => onModalClose?.(),
    onSuccessWithRemainingRefunds: () => {
      // Modal stays open when there are remaining refunds
      // Clear form selection so user must select a new item
      form.setFieldValue('paymentType', undefined);
      form.setFieldValue('withdrawalAmount', undefined);
    },
  });

  /**
   * Handles switching between withdrawal and refund modes
   * Resets form fields when switching to prevent stale data
   * @param {('withdrawal' | 'refund')} newMode - The mode to switch to
   */
  const handleModeSwitch = (newMode: 'withdrawal' | 'refund') => {
    onModeChange?.(newMode);
    // Reset form when switching modes
    form.setFieldValue('paymentType', undefined);
    form.setFieldValue('withdrawalAmount', undefined);
    form.setFieldValue('bankAccountId', undefined);
  };

  /**
   * Handles refund submission when in refund mode
   * Validates that payment type and amount are selected before submitting
   */
  const handleRefundSubmit = () => {
    if (isRefundMode && paymentType && withdrawalAmount) {
      submitRefund({
        paymentMethod: paymentType,
        amount: withdrawalAmount,
      });
    }
  };

  // Coordinated loading state - wait for all relevant data sources
  const isLoadingPaymentMethods = isRefundMode
    ? isFetchingRefundableBreakdown
    : isFetchingAvailablePaymentMethods || (isAeropayEnabled && isLoadingBankAccounts);

  const isContinueButtonDisabled =
    isCurrentPaymentTypeDisabled ||
    !paymentType ||
    (isRefundMode && isSubmitting) ||
    (isRefundMode && hasConsistencyError) || // Disable button when there's a consistency error
    (isRefundMode && (!refundableBreakdown?.items?.length || !withdrawalAmount)); // Disable button when no refundable items or no amount selected

  const getHeaderConfig = () => {
    if (isRefundMode) {
      return {
        title: t('refund.title'),
        copy: t('refund.copy'),
      };
    }
    return {
      title: t('withdrawal.form.select.title'),
      copy: t('withdrawal.form.select.copy'),
    };
  };

  const getButtonConfig = () => {
    const getText = () => {
      if (isRefundMode && hasConsistencyError) {
        return t('refund.selectRefund'); // Don't show amount when there's an error
      }
      if (isRefundMode && withdrawalAmount && refundableBreakdown?.items?.length > 0) {
        // Only show amount if there are refundable items available
        return t('refund.refundAmount', {
          amount: `$${withdrawalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        });
      }
      if (isRefundMode) {
        return t('refund.selectRefund');
      }
      return t('withdrawal.form.actions.continue');
    };

    return {
      text: getText(),
      disabled: isContinueButtonDisabled,
      onClick: isRefundMode ? handleRefundSubmit : undefined,
    };
  };

  const getBalanceConfig = () => {
    if (isRefundMode) {
      return {
        variant: 'refundableBalance' as const,
        showRefundable: false,
      };
    }

    return {
      variant: 'withdrawableBalance' as const,
      showRefundable: walletBalance?.refundableBalance > 0,
      refundableBalance: walletBalance?.refundableBalance,
      isLoadingWalletBalance,
      onRefundClick: () => handleModeSwitch('refund'),
    };
  };

  const getFeeDescriptions = () => {
    if (isRefundMode || !paidPaymentTypes) {
      return [];
    }

    return paidPaymentTypes
      .map(([type, settings], index) => {
        if (settings.isFree) {
          return null;
        }
        // TypeScript knows settings has description when isFree is false
        const paidSettings = settings as { isFree: false; description: string };
        return {
          number: index + 1,
          description: paidSettings.description,
          paymentType: type,
        };
      })
      .filter(Boolean);
  };

  return {
    isRefundMode,
    isSubmitting,
    refundableBreakdown,
    isFetchingRefundableBreakdown,
    hasConsistencyError, // NEW: Error state for balance consistency issues
    isLoadingPaymentMethods,
    bankAccounts,
    feeDescriptions: getFeeDescriptions(),
    headerConfig: getHeaderConfig(),
    buttonConfig: getButtonConfig(),
    balanceConfig: getBalanceConfig(),
    handlers: {
      onModeSwitch: handleModeSwitch,
      onRefundSubmit: handleRefundSubmit,
    },
  };
}
